<template>
	<div>
		<j-expendable-box class="bg5 b1 bc7 r2">
			<div slot="header" class="pl3 pr3 pt4 pb4 lh125 fs4 c1 fw7">
				{{ $t("facts") }}
			</div>

			<div slot="body">
				<div class="flex xs-12 xs-right bb1 bc7 p3">
					<j-button type="tertiary" @click="openAddModal">
						<crm-icon name="add" color="c2" />Add fact
					</j-button>
				</div>
				<j-list :list="computedFields">
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<!-- Label -->
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">
							{{ item.type }}
						</div>

						<!-- Value -->
						<div class="flex xs-4 xs-left xs-middle lh125">
							{{
								item.dataType === "arrayValue"
									? "Array fact"
									: item.dataType === "objectValue"
									? "Object fact"
									: item.value
							}}
						</div>

						<!-- Actions -->
						<div class="flex xs-2 xs-right xs-middle">
							<crm-icon
								name="edit"
								color="c2"
								class="cu2"
								v-if="item.hasUpdate"
								@click.native="openUpdateModal(item)"
							/>
							<crm-icon
								name="delete"
								color="cError"
								class="ml2 cu2"
								v-if="item.hasDelete"
								@click.native="removeFact(item.id)"
							/>
						</div>
					</div>
				</j-list>
				<!-- no data -->
				<div v-if="!computedFields.length" class="xs-12" slot="body">
					<div class="xs-12 flex xs-center xs-middle p4 fs3 c1">
						{{ $t("errors.no_data") }}
					</div>
				</div>
			</div>
		</j-expendable-box>
		<!-- Modal -->
		<core-modal name="factModal" :headline="$t('case_fact')">
			<div class="p4">
				<core-form-input
					class="mb4"
					name="dataType"
					componentName="j-base-select"
					:options="[
						{ name: 'Text', value: 'stringValue' },
						{ name: 'Number', value: 'numberValue' },
						{ name: 'Boolean', value: 'booleanValue' },
						{ name: 'Date', value: 'dateValue' },
						{ name: 'Array', value: 'arrayValue' },
						{ name: 'Object', value: 'objectValue' }
					]"
					:label="$t('fact_type')"
					@input="onDataTypeInput"
					v-model="form.dataType"
				/>
				<core-form-input
					class="mb4"
					name="type"
					componentName="j-base-input-text"
					:label="$t('fact_label')"
					v-model="form.type"
				/>
				<core-form-input
					class="mt2"
					v-if="form.dataType"
					name="value"
					:componentName="valueComponentName"
					:label="$t('value')"
					v-model="form.value"
				/>
				<div class="xs-12 xs-right flex">
					<j-button
						class="mt2"
						v-if="
							form.dataType === 'arrayValue' || form.dataType === 'objectValue'
						"
						type="tertiary"
						size="xsmall"
						@click="prepopulateDefault"
						>{{ $t("prepopulate_default") }}</j-button
					>
				</div>
				<j-button
					v-if="formIsValid"
					@click="modalAction"
					:isLoading="isLoading"
					class="mt4"
					>{{ $t("save") }}</j-button
				>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JList from "@/components/JList"
import JExpendableBox from "@/components/JExpendableBox"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreModal from "@/modules/core/components/CoreModal"
import CrmIcon from "@/components/CrmIcon"
import JButton from "@/components/JButton"

import dayjs from "dayjs"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

const presetObjectValue = [{ key: "key", value: { stringValue: "" } }]
const presetArrayValue = [{ stringValue: "" }]

export default {
	components: {
		CoreModal,
		JList,
		JExpendableBox,
		CrmIcon,
		JButton,
		CoreFormInput
	},

	data() {
		return {
			isLoading: false,
			form: {
				type: null,
				value: null,
				id: null
			},
			modalAction: null
		}
	},

	computed: {
		formIsValid() {
			return (
				this.form.value !== null &&
				this.form.dataType &&
				this.form.type &&
				this.form.value
			)
		},

		computedFields() {
			const facts = this.$store.getters["caseManagement/offer"]?.facts
			return facts.map(fact => {
				const dataType = fact.value.stringValue
					? "stringValue"
					: fact.value.numberValue !== null &&
					  fact.value.numberValue !== undefined
					? "numberValue"
					: fact.value.dateValue !== null && fact.value.dateValue !== undefined
					? "dateValue"
					: fact.value.booleanValue !== null &&
					  fact.value.booleanValue !== undefined
					? "booleanValue"
					: fact.value.arrayValue !== null &&
					  fact.value.arrayValue !== undefined
					? "arrayValue"
					: "objectValue"
				return {
					type: fact.type,
					id: fact.id,
					value:
						dataType === "stringValue"
							? fact.value.stringValue
							: dataType === "numberValue"
							? fact.value.numberValue
							: dataType === "booleanValue"
							? fact.value.booleanValue
							: dataType === "dateValue"
							? dayjs(fact.value.dateValue).format(
									this.$store.state.settings.dateFormat
							  )
							: dataType === "arrayValue"
							? JSON.stringify(fact.value.arrayValue)
							: dataType === "objectValue"
							? JSON.stringify(fact.value.objectValue)
							: null,
					dataType,
					hasUpdate: true,
					hasDelete: true
				}
			})
		},

		valueComponentName() {
			return this.form.dataType === "numberValue"
				? "j-base-input-number"
				: this.form.dataType === "dateValue"
				? "j-base-date-picker-simple"
				: this.form.dataType === "booleanValue"
				? "j-base-toggle"
				: this.form.dataType === "stringValue"
				? "j-base-input-text"
				: "core-monaco-editor"
		}
	},

	methods: {
		onDataTypeInput() {
			if (this.form.dataType === "booleanValue") this.form.value = false
			else this.form.value = null
		},

		openAddModal() {
			this.modalAction = this.addFact
			this.form = {
				type: null,
				value: null,
				id: null
			}
			this.$modal.show("factModal")
		},

		openUpdateModal(item) {
			this.form = { ...item }
			this.modalAction = this.updateFact
			this.$modal.show("factModal")
		},

		prepopulateDefault() {
			if (this.form.dataType === "arrayValue")
				this.form.value = JSON.stringify(presetArrayValue)
			if (this.form.dataType === "objectValue")
				this.form.value = JSON.stringify(presetObjectValue)
		},

		async updateFact() {
			const query = gql`
				mutation updateFactOfProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: updateFactInput!
					$factId: String!
				) {
					updateFactOfProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
						factId: $factId
					) {
						errors
						status
						errors_2 {
							message
							code
						}
					}
				}
			`

			const formValue =
				this.form.dataType === "arrayValue" ||
				this.form.dataType === "objectValue"
					? JSON.parse(this.form.value)
					: this.form.value

			const variables = {
				caseId: this.$store.state.caseManagement.case.id,
				proposalId: this.$store.state.caseManagement.proposalId,
				offerId: this.$store.state.caseManagement.offerId,
				input: {
					type: this.form.type,
					value: {
						[this.form.dataType]: formValue
					}
				},
				factId: this.form.id
			}

			const res = await api({ query, variables })

			if (res?.data?.updateFactOfProposalOffer?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateFactOfProposalOffer?.errors ||
							res?.data?.updateFactOfProposalOffer?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else {
				const factId = this.form.id
				const stateFilter = function(state) {
					return state.proposals
						?.find(item => item.id === state.proposalId)
						?.basket?.find(item => item.id === state.offerId)
						?.facts?.find(fact => fact.id === factId)
				}
				// Update type
				this.$store.commit("caseManagement/UPDATE_CASE", {
					actionType: "update",
					fieldName: "type",
					value: this.form.type,
					stateFilter
				})
				// Update value
				this.$store.commit("caseManagement/UPDATE_CASE", {
					actionType: "update",
					fieldName: "value",
					value: {
						[this.form.dataType]: formValue
					},
					stateFilter
				})
				this.$modal.hide("factModal")
			}
		},

		async addFact() {
			const query = gql`
				mutation addFactToProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: addFactInput!
				) {
					addFactToProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
					) {
						errors
						status
						createdStatus {
							id
						}
					}
				}
			`

			const formValue =
				this.form.dataType === "arrayValue" ||
				this.form.dataType === "objectValue"
					? JSON.parse(this.form.value)
					: this.form.value

			const variables = {
				caseId: this.$store.state.caseManagement.case.id,
				proposalId: this.$store.state.caseManagement.proposalId,
				offerId: this.$store.state.caseManagement.offerId,
				input: {
					type: this.form.type,
					value: {
						[this.form.dataType]: formValue
					}
				},
				factId: this.form.id
			}

			const res = await api({ query, variables })

			if (res?.data?.addFactToProposalOffer?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addFactToProposalOffer?.errors ||
							res?.data?.addFactToProposalOffer?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else {
				const stateFilter = function(state) {
					return state.proposals
						?.find(item => item.id === state.proposalId)
						?.basket?.find(item => item.id === state.offerId)
				}
				this.$store.commit("caseManagement/UPDATE_CASE", {
					actionType: "push",
					fieldName: "facts",
					value: {
						type: this.form.type,
						id: res.data.addFactToProposalOffer?.createdStatus?.id,
						value: {
							[this.form.dataType]: formValue
						}
					},
					stateFilter
				})
				this.$modal.hide("factModal")
			}
		},

		async removeFact(factId) {
			const query = gql`
				mutation removeFactFromProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$factId: String!
				) {
					removeFactFromProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						factId: $factId
					) {
						errors
						status
						errors_2 {
							code
							message
						}
					}
				}
			`

			const variables = {
				caseId: this.$store.state.caseManagement.case.id,
				proposalId: this.$store.state.caseManagement.proposalId,
				offerId: this.$store.state.caseManagement.offerId,
				factId
			}

			const res = await api({ query, variables })

			if (res?.data?.removeFactFromProposalOffer?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeFactFromProposalOffer?.errors ||
							res?.data?.removeFactFromProposalOffer?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else {
				const stateFilter = function(state) {
					return state.proposals
						?.find(item => item.id === state.proposalId)
						?.basket?.find(item => item.id === state.offerId)?.facts
				}

				this.$store.commit("caseManagement/UPDATE_CASE", {
					actionType: "remove",
					fieldName: "id",
					value: factId,
					stateFilter
				})
				this.$modal.hide("factModal")
			}
		}
	}
}
</script>
